import { assign, isArray, isNumber, hasIn } from 'lodash';
import * as actionTypes from './umoGalleryActionTypes';
import { showSearchAsMain, showHighlightsAsMain, setMetaUmoId } from './mainUI';
import appConfig from '../appConfig';
import piction from '../utils/piction';
import { getLabelFromAppConfig } from '../utils/uiUtils';
import {
  CategoryTag,
  DynamicTag,
  SearchParams,
  WorkspaceTag,
  SearchTag,
  HighlightSetTag,
} from '../utils/searchUtils';
import {
  defaultCallData,
  errorMessages,
  searchExistsMessage,
} from '../constants/umoGallery';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired } from './session';
import {
  UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR,
  UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE,
} from '../constants/umoModifications';

export const setGalleryType = (gType) => ({
  type: actionTypes.UMOGALLERY_SET_GALLERY_TYPE,
  gType,
});

export const retrieveUMOsRequest = (
  searchParams,
  requestPromise,
  umoGalleryOptions = {},
  options = {}
) => ({
  type: options.overrideActionType || actionTypes.UMOGALLERY_SEARCH_RETRIEVE,
  searchParams,
  requestPromise,
  ...umoGalleryOptions,
});

export const retrieveUMOsSuccess = (umoList = [], queryData) => ({
  type: actionTypes.UMOGALLERY_SEARCH_SUCCESS,
  umoList,
  queryData,
});

export const retrieveUMOsFailure = (err) => ({
  type: actionTypes.UMOGALLERY_SEARCH_FAILURE,
  err,
});

/**
 * This is the main function that retrieves the UMOs. All other queries such as category/workspace
 * retrieval, sorting, etc. will go through this function.
 * @param searchParams SearchParams instance used to build the SEARCH part of the image query
 * @param options
 */
export const retrieveUMOs = (searchParams, options = {}) =>
  function retrieveUMOsDispatch(dispatch, getState) {
    // since retrieval of umos has been triggered, assume that the main content should show them,
    // so show the search area (search bar and umo gallery)
    if (options.showHighlights === true && appConfig.useHighlights)
      dispatch(showHighlightsAsMain());
    else dispatch(showSearchAsMain());

    const store = getState().umoGallery;
    const umoGalleryOptions = {};
    let umosPerPage;
    let sortBy;
    let sortByIsDescending;
    let pageOffset;
    let metatags;
    let newSearchParams;
    let alternates;
    let collectionID;

    umoGalleryOptions.prevSearch = store.get('searchParams');

    if (options.metatags) {
      metatags = options.metatags;
      umoGalleryOptions.metatags = metatags;
    } else if (store.get('metatags')) {
      metatags = store.get('metatags');
    }

    if (options.umosPerPage) {
      umosPerPage = options.umosPerPage;
      umoGalleryOptions.umosPerPage = umosPerPage;
    } else if (store.get('umosPerPage')) {
      umosPerPage = store.get('umosPerPage');
    }

    if (
      store.get('searchParams') &&
      (options.useExistingSearch || !searchParams)
    ) {
      newSearchParams = store
        .get('searchParams')
        .mergeWith((currentValue, newValue) => {
          if (newValue === null) return undefined;
          else if (newValue === undefined) return currentValue;
          return newValue;
        }, searchParams);
    } else {
      newSearchParams = searchParams;
    }

    // set the gallery type (category, workspace, general etc)
    if (newSearchParams.getSearchType() !== store.get('galleryType'))
      dispatch(setGalleryType(searchParams.getSearchType()));

    // if no pageNumber override is given and the search parameters will change,
    // then reset the page number back to 1
    if (
      !{}.hasOwnProperty.call(options, 'pageNumber') &&
      newSearchParams !== store.get('searchParams')
    ) {
      pageOffset = 1;

      // don't need to reset if it's already 1
      if (store.get('pageNumber') !== 1) {
        umoGalleryOptions.pageNumber = 1;
      }
    } else if (
      {}.hasOwnProperty.call(options, 'pageNumber') ||
      store.get('pageNumber')
    ) {
      let pageNumber = store.get('pageNumber');

      if ({}.hasOwnProperty.call(options, 'pageNumber')) {
        if (!isNumber(options.pageNumber)) {
          throw new Error(errorMessages.INVALID_ARGS_PAGE);
        } else if (
          options.pageNumber < 1 ||
          options.pageNumber > Math.ceil(store.get('umoTotal') / umosPerPage)
        ) {
          throw new Error(errorMessages.INVALID_ARGS_PAGE_RANGE);
        }
        pageNumber = options.pageNumber;
        umoGalleryOptions.pageNumber = pageNumber;
      }

      pageOffset = (pageNumber - 1) * umosPerPage + 1;
    }

    if (options.sortBy || store.get('sortBy')) {
      let isDescending;
      if (options.sortBy !== 'NONE') {
        if (options.sortByIsDescending !== undefined) {
          isDescending = options.sortByIsDescending;
        } else {
          isDescending = store.get('sortByIsDescending') || false;
        }

        sortByIsDescending = isDescending ? 'DESCENDING' : 'ASCENDING';

        if (options.sortBy) {
          sortBy = options.sortBy;
          umoGalleryOptions.sortBy = sortBy;
          umoGalleryOptions.sortByIsDescending = isDescending;
        } else {
          sortBy = store.get('sortBy');
        }
      }
    }

    if (appConfig.alternateReps && appConfig.alternateReps.length > 0) {
      alternates = {
        CHECK_ALTERNATE_REPS: 'TRUE',
        ALTERNATE_METATAG: appConfig.alternateReps[0],
        ALTERNATE_COLLECTION: appConfig.alternateReps[1],
        ALTERNATE_EXCLUDE_RELATIONAL: appConfig.alternateExcRelational,
      };
    } else alternates = { ENABLE_ALTERNATE: 'TRUE' };

    const overrideCallData = assign(
      defaultCallData.toJS(),
      { zoomify: true },
      umosPerPage && { MAXROWS: umosPerPage },
      sortBy !== 'NONE' && {
        ORDERBY: sortBy,
        SORT_ASCENDING: sortByIsDescending,
      },
      pageOffset && { START: pageOffset },
      metatags && { METATAGS: metatags },
      newSearchParams.metadataList &&
      newSearchParams.metadataList.length > 0 && { IS_ADVANCED: 'TRUE' },
      alternates,
      collectionID,
      appConfig.enableSortCategory && {
        SHOW_SORT_VAL: 'TRUE',
        RETURN_SORT_AS_NUMBERS: 'TRUE',
      }
    );

    let promise;

    if (!newSearchParams || newSearchParams.toSearchQuery() === '') {
      promise = Promise.resolve();
    } else {
      promise = piction.getUMOs(
        newSearchParams.toSearchQuery(),
        overrideCallData
      );
    }

    promise
      .then((res) => {
        dispatch(
          retrieveUMOsSuccess(
            hasIn(res, 'res.a.r') ? res.res.a.r : [],
            hasIn(res, 'res.a.s') ? res.res.a.s[0] : {}
          )
        );
        return res;
      })
      .catch((err) => {
        if (
          err.message &&
          (err.message.includes(SESSION_EXPIRED_MESSAGE) ||
            err.message.includes('You are not logged in'))
        ) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveUMOsFailure(err));
        }
        return err;
      });
    dispatch(
      options.overrideActionCreator
        ? options.overrideActionCreator(
          newSearchParams,
          promise,
          umoGalleryOptions
        )
        : retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions)
    );
    return promise;
  };

export const retrieveSimpleSearch = (searchQuery, typeFilter, tagList) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery,
      typeFilter: typeFilter || null,
      metadataList: null,
      tagList: tagList || null,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveAdvancedSearch = (
  metadataList,
  typeFilter = null,
  collectionID = null,
  queryName
) =>
  retrieveUMOs(
    new SearchParams({
      metadataList,
      typeFilter,
      tagList: null,
      searchQuery: null,
      collectionID,
    }),
    {
      queryName,
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveSavedSearch = (savedSearch) =>
  retrieveUMOs(
    new SearchParams({
      metadataList: null,
      typeFilter: null,
      tagList: null,
      searchQuery: null,
      savedSearch,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveBaseSearch = () =>
  retrieveUMOs(
    new SearchParams(appConfig.initialQuery ? {
      tagList: [
        new SearchTag(
          'search',
          appConfig.initialQuery?.searchKeyword,
          appConfig.initialQuery?.value,
          getLabelFromAppConfig(appConfig.initialQuery),
          getLabelFromAppConfig(appConfig.initialQuery, 'description')
        ),
      ],
      displayTags: !!(
        appConfig.initialQuery.label || appConfig.initialQuery.labelLocale
      ),
    } : undefined),
    { showHighlights: appConfig.useHighlights }
  )

export const retrieveCategory = (categoryID, label, description, search) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery: null,
      tagList: search
        ? [new DynamicTag(search, label, description)]
        : [new CategoryTag(categoryID, label, description)],
      metadataList: null,
      filterByList: null,
      typeFilter: null,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveWorkspace = (workspaceID, label, description) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery: null,
      tagList: [new WorkspaceTag(workspaceID, label, description)],
      metadataList: null,
      filterByList: null,
      typeFilter: null,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveHighlightSet = (categoryID, label, description) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery: null,
      tagList: [new HighlightSetTag(categoryID, label, description)],
      metadataList: null,
      filterByList: null,
      typeFilter: null,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveThesaurusTermSearch = (termID, termName, nodeID) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery: null,
      tagList: [
        new DynamicTag(
          `thes_hierrestrict:"${nodeID},${termID},ACTIVE"`,
          `THESAURUS TERM: ${termName}`
        ),
      ],
      metadataList: null,
      filterByList: null,
      typeFilter: null,
    }),
    {
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const retrieveFilteredUMOs = (filterByList) =>
  retrieveUMOs(
    new SearchParams({
      filterByList,
    }),
    {
      useExistingSearch: true,
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

export const updateUMOsPerPageRequest = (umosPerPage, requestPromise) => ({
  type: actionTypes.UMOGALLERY_UPDATE_UMOSPERPAGE_RETRIEVE,
  umosPerPage,
  pageNumber: 1,
  requestPromise,
});

export const updateUMOsPerPage = (umosPerPage) =>
  retrieveUMOs(new SearchParams(), {
    umosPerPage,
    pageNumber: 1,
    useExistingSearch: true,
    overrideActionCreator: (newSearchParams, promise) =>
      updateUMOsPerPageRequest(umosPerPage, promise, {
        overrideActionType: actionTypes.UMOGALLERY_FILTER_RETRIEVE,
      }),
  });

export const updateSortByRequest = (
  sortBy,
  sortByIsDescending,
  requestPromise
) => ({
  type: actionTypes.UMOGALLERY_UPDATE_SORTBY_RETRIEVE,
  sortBy,
  sortByIsDescending,
  requestPromise,
});

export const updateSortBy = (sortBy, sortByIsDescending) =>
  retrieveUMOs(new SearchParams(), {
    sortBy,
    sortByIsDescending,
    useExistingSearch: true,
    overrideActionCreator: (newSearchParams, promise) =>
      updateSortByRequest(sortBy, sortByIsDescending, promise, {
        overrideActionType: actionTypes.UMOGALLERY_FILTER_RETRIEVE,
      }),
  });

export const updatePageNumberRequest = (pageNumber, requestPromise) => ({
  type: actionTypes.UMOGALLERY_UPDATE_PAGE_RETRIEVE,
  pageNumber,
  requestPromise,
});

export const updatePageNumber = (pageNumber) =>
  retrieveUMOs(new SearchParams(), {
    pageNumber,
    useExistingSearch: true,
    overrideActionCreator: (newSearchParams, promise) =>
      updatePageNumberRequest(pageNumber, promise, {
        overrideActionType: actionTypes.UMOGALLERY_FILTER_RETRIEVE,
      }),
  });

export const cancelAllRequests = () => ({
  type: actionTypes.UMOGALLERY_CANCEL_ALL_REQUESTS,
});

export const selectToggle = (umoIDs = [], isSelect) => ({
  type: actionTypes.UMOGALLERY_SELECT_TOGGLE,
  umoIDList: isArray(umoIDs) ? umoIDs : [umoIDs],
  isSelect,
});

export const setSelectedUMOIDs = (umoIDs = [], isSelect) => ({
  type: actionTypes.UMOGALLERY_SET_SELECTED_UMOIDS,
  umoIDList: isArray(umoIDs) ? umoIDs : [umoIDs],
  isSelect,
});

export const setLastSelectedUMOID = (umoID) => ({
  type: actionTypes.UMOGALLERY_SET_LAST_SELECTED_UMO_ID,
  umoID,
});

export const toggleFilterMenu = (value) => ({
  type: actionTypes.UMOGALLERY_TOGGLE_FILTER_MENU,
  value,
});

export const selectPage = () => ({
  type: actionTypes.UMOGALLERY_SELECT_PAGE,
});

export const selectSearch = () => ({
  type: actionTypes.UMOGALLERY_SELECT_SEARCH,
});

export const deselectAll = () => ({
  type: actionTypes.UMOGALLERY_DESELECT_ALL,
});

export const clearSearch = () => ({
  type: actionTypes.UMOGALLERY_CLEAR_SEARCH,
});

export const updateSavedSearches = (searchArray) => ({
  type: actionTypes.ADVANCED_SEARCH_SAVE,
  searchArray,
});

export const retrieveSavedSearchList = () =>
  function retrieveSavedSearchListDispatch(dispatch) {
    return piction
      .request('image_query', { QUERY_NAME_SEARCH: '%' }, null)
      .then((res) => {
        if (res.a && res.a.RETURN_MESSAGE === SESSION_EXPIRED_MESSAGE) {
          dispatch(sessionExpired(res));
        } else {
          dispatch(updateSavedSearches(res.a.qry_result));
        }
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveUMOsFailure(err));
        }
        return err;
      });
  };

export const deleteSavedSearch = (searchID) =>
  function deleteSavedSearchDispatch(dispatch) {
    return piction
      .request('image_query', { DELETE_QUERY: searchID }, null)
      .then((res) => {
        dispatch(retrieveSavedSearchList());
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveUMOsFailure(err));
        }
        return err;
      });
  };

export const saveSearchFailure = (err) => ({
  type: actionTypes.ADVANCED_SEARCH_SAVE_FAILURE,
  err,
});

export const saveSearch = (
  metadataList,
  typeFilter = null,
  collectionID,
  queryName
) =>
  function saveSearchDispatch(dispatch) {
    const overrideCallData = assign(
      { QUERY_NAME: queryName },
      // { REPLACE_QUERY: override },
      metadataList && metadataList.length > 0 && { IS_ADVANCED: 'TRUE' },
      { IMAGE_TYPE: typeFilter }
    );
    const searchParams = new SearchParams({
      metadataList,
      typeFilter,
      tagList: null,
      searchQuery: null,
      collectionID,
    });

    const data = assign(
      {
        SEARCH: searchParams.toSearchQuery(),
        QUERY_NAME: queryName,
      },
      defaultCallData.toJS(),
      metadataList && metadataList.length > 0 && { IS_ADVANCED: 'TRUE' }
    );

    return piction
      .request('image_query', data, null)
      .then((res) => {
        if (
          res &&
          res.a &&
          res.a.RETURN_MESSAGE &&
          res.a.RETURN_MESSAGE[0]._ === searchExistsMessage
        ) {
          return 'save exists';
        }
        dispatch(retrieveSavedSearchList());
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
          // } else if (err.message && err.message.includes(searchExistsMessage)) {
          //   return 'save exists';
        } else {
          dispatch(saveSearchFailure(err));
        }
        return err;
      });
  };

export const updatePrevSearchList = () => ({
  type: actionTypes.UMOGALLERY_UPDATE_PREV_SEARCH,
});

export const retrievePrevSearch = (searchParams) =>
  function retrievePrevSearchDispatch(dispatch) {
    dispatch(
      retrieveUMOs(searchParams, {
        overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
          retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
      })
    );
    dispatch(updatePrevSearchList());
    dispatch(setMetaUmoId(null));
  };

export const retrieveExternalSearch = (searchQuery) =>
  function retrieveExternalSearchDispatch(dispatch) {
    const tag = new DynamicTag(searchQuery, 'Search Result');
    dispatch(retrieveSimpleSearch(null, null, [tag]));
  };

// sort by is set to none, because it runs over the sort params
// SHOW_SORT_VAL and RETURN_SORT_AS_NUMBERS, which are necessary for this
export const retrieveSortUMOs = (categoryID, categoryName) =>
  retrieveUMOs(
    new SearchParams({
      searchQuery: null,
      tagList: [new CategoryTag(categoryID, categoryName)],
      metadataList: null,
      filterByList: null,
      typeFilter: null,
    }),
    {
      umosPerPage: 100,
      sortBy: 'NONE',
      overrideActionCreator: (newSearchParams, promise, umoGalleryOptions) =>
        retrieveUMOsRequest(newSearchParams, promise, umoGalleryOptions),
    }
  );

// action to set the selected category from the panel tree
export const setSelectedCategory = (category) => ({
  type: actionTypes.UMOGALLERY_SET_SELECTED_CATEGORY,
  category,
});

export const executeEditImageDownload = (
  CSRF,
  SURL,
  editType,
  umoId,
  options
) =>
  function executeEditImageDownloadDispatch() {
    const baseRequestData = {
      n: 'NWS_IMAGE_PROCESS',
      umo_id: umoId,
      DOWNLOAD: 'True',
      DOWNLOAD_IMAGE: 'ORIGINAL',
      DOWNLOAD_IMAGE_ID: '1',
      DOWNLOAD_AS_ATTACHMENT: 'ATTACH',
      CSRF,
      SURL,
    };
    let requestData = baseRequestData;

    if (editType === UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE) {
      requestData = {
        ...requestData,
        action: 'ROTATE',
        ROTATE: options.rotationAngle,
      };
    } else if (editType === UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR) {
      requestData = {
        ...requestData,
        action: 'ADJUST',
        EXTRA_IMAGEMAGICK: options.flipFlops,
      };
    }
    const queryParams = new URLSearchParams(requestData);
    window.open(`${appConfig.hostURL}!soap.jsonget?${queryParams}`, '_blank');
  };
